import React from 'react';
import PropTypes from 'prop-types';

import Complete from './complete';
import Progress from './progress';
import Password from './password';
import Expired from './expired';
import Invalid from './invalid';
import Spam from './spam';
import Files from './files';
import Blocked from './blocked';

import Store from '@app/store';
import {
  prepareTransferDownload,
  fetchTransferDownloadDetails,
} from '@app/transfer-bundle/store/download-actions';
import { TRANSFER_STATES } from '@app/transfer-bundle/constant/transfer-states';

// If the transfer is processing, we'll poll for updates.
const POLL_INTERVAL = 2500;
export default class Downloader extends React.Component {
  static propTypes = {
    id: PropTypes.string.isRequired,
    secret: PropTypes.string.isRequired,
    recipient_id: PropTypes.string,
    file_id: PropTypes.string,
  };

  /**
   * Binds all private methods
   * @param  {Object} props Component props
   */
  constructor(props) {
    super(props);

    this.pollTransferDetails = this.pollTransferDetails.bind(this);
  }

  // When this component gets mounted, we'll see if there is already sideloaded information
  // available to speed up the process.
  componentDidMount() {
    // By default we'll have an empty transfer that is processing.
    let transferDetails = {
      state: TRANSFER_STATES.PROCESSING,
    };

    // Get the sideloaded Transfer information and remove the global object.
    if (
      window['_preloaded_transfer_'] &&
      typeof window['_preloaded_transfer_'] === 'object'
    ) {
      transferDetails = window['_preloaded_transfer_'];
      window['_preloaded_transfer_'] = null;
    }

    // Create a new Transfer object with the sideloaded data.
    Store.dispatch(prepareTransferDownload(transferDetails));

    // Get the transfer from our store
    const { transfer } = Store.getState();

    // No longer processing, done or error
    if (
      transfer.state !== TRANSFER_STATES.PROCESSING &&
      transfer.state !== TRANSFER_STATES.PASSWORD_PROTECTED
    ) {
      return;
    }

    // Load Transfer Details from the API if it's still processing
    this.pollTransferDetails();
  }

  // When this component gets unmounted, we can stop the polling timer if it's active
  componentWillUnmount() {
    clearTimeout(this.timer);
  }

  componentDidUpdate() {
    // Stop timer if the state is already downloadable
    const { transfer } = Store.getState();
    if (transfer.state === TRANSFER_STATES.DOWNLOADABLE) {
      clearTimeout(this.timer);
    }
  }

  // Load Transfer details for our current transfer
  pollTransferDetails() {
    const { transfer, download } = Store.getState();

    if (
      transfer.state === TRANSFER_STATES.PASSWORD_PROTECTED &&
      !transfer.password
    ) {
      // Try again after the INTERVAL
      clearTimeout(this.timer);
      this.timer = setTimeout(this.pollTransferDetails, POLL_INTERVAL);
      return;
    }

    // If the current transfer state is not processing, we don't have to poll
    if (transfer.state !== TRANSFER_STATES.PROCESSING) {
      clearTimeout(this.timer);

      return;
    }

    // Only send a new request if we're not still waiting for one.
    if (!download.isPending) {
      // Load the Transfer data from our API
      const password = transfer.password || false;

      Store.dispatch(
        fetchTransferDownloadDetails({
          id: this.props.id,
          secret: this.props.secret,
          recipientId: this.props.recipient_id,
          password,
        })
      );
    }

    // Try again after the INTERVAL
    clearTimeout(this.timer);
    this.timer = setTimeout(this.pollTransferDetails, POLL_INTERVAL);
  }

  render() {
    const { transfer } = Store.getState();
    let component = null;
    if (!transfer.state) {
      return false;
    }

    switch (transfer.state) {
      // Transfer is still in processing.
      case TRANSFER_STATES.PROCESSING:
      case TRANSFER_STATES.INIT:
        component = (
          <Files
            isReady={false}
            transfer={transfer}
            id={this.props.id}
            secret={this.props.secret}
            recipient={this.props.recipient_id}
          />
        );
        break;

      // The transfer is no longer available for download
      case TRANSFER_STATES.EXPIRED:
        component = <Expired transfer={transfer} />;
        break;

      // This is not a valid transfer (404 I guess)
      case TRANSFER_STATES.INVALID:
        component = <Invalid transfer={transfer} />;
        break;

      // This transfer was reported as spam
      case TRANSFER_STATES.SPAM:
        component = <Spam transfer={transfer} />;
        break;

      case TRANSFER_STATES.PASSWORD_PROTECTED:
        component = (
          <Password
            transferId={transfer.id}
            secret={this.props.secret}
            recipient={this.props.recipient_id}
          />
        );
        break;

      // The transfer is downloadable, show a summary of files
      case TRANSFER_STATES.DOWNLOADABLE:
        component = (
          <Files
            isReady={true}
            transfer={transfer}
            id={this.props.id}
            secret={this.props.secret}
            recipient={this.props.recipient_id}
            file_id={this.props.file_id}
          />
        );
        break;

      // Download has started.
      case TRANSFER_STATES.DOWNLOADING:
        // You started the download
        component = <Progress transfer={transfer} />;
        break;

      // Download finished
      case TRANSFER_STATES.FINISHED:
        // The download has started in the browsers but is finished from our point of view
        component = <Complete />;
        break;

      case TRANSFER_STATES.BLOCKED:
        component = <Blocked />;
        break;
    }

    return <div>{component}</div>;
  }
}
